/**
 * Offline UI Components
 * 
 * React components for managing UI state in offline scenarios including
 * loading states, error states, sync indicators, and offline notifications.
 */

import React from 'react';
import { AlertCircle, Wifi, WifiOff, RefreshCw, CheckCircle, XCircle, Clock } from 'lucide-react';
import { useOfflineState } from '../useOfflineState';
import { useNetworkStatus, useSyncStatus, useLoadingState, useErrorState } from '../hooks/useOfflineState';

/**
 * Offline indicator component
 */
export function OfflineIndicator() {
  const { isOnline, connectionQuality, showOfflineIndicator } = useNetworkStatus();

  if (!showOfflineIndicator && isOnline) return null;

  const getIndicatorColor = () => {
    if (!isOnline) return 'bg-red-500';
    if (connectionQuality === 'poor') return 'bg-yellow-500';
    return 'bg-green-500';
  };

  const getIndicatorText = () => {
    if (!isOnline) return 'Offline';
    if (connectionQuality === 'poor') return 'Poor Connection';
    return 'Online';
  };

  const getIcon = () => {
    if (!isOnline) return <WifiOff className="w-4 h-4" />;
    return <Wifi className="w-4 h-4" />;
  };

  return (
    <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-3 py-2 rounded-lg text-white text-sm font-medium ${getIndicatorColor()}`}>
      {getIcon()}
      <span>{getIndicatorText()}</span>
    </div>
  );
}

/**
 * Sync progress indicator
 */
export function SyncProgressIndicator() {
  // Disabled - sync progress should not be shown to users
  return null;
}

/**
 * Sync status badge
 */
export function SyncStatusBadge() {
  const { isSyncing, lastSyncTime, syncError, pendingOperations, failedOperations } = useSyncStatus();
  const { isOnline } = useNetworkStatus();

  const getStatusInfo = () => {
    if (syncError) {
      return {
        icon: <XCircle className="w-4 h-4" />,
        text: 'Sync Error',
        color: 'bg-red-100 text-red-800 border-red-200'
      };
    }
    
    if (isSyncing) {
      return {
        icon: <RefreshCw className="w-4 h-4 animate-spin" />,
        text: 'Syncing',
        color: 'bg-blue-100 text-blue-800 border-blue-200'
      };
    }
    
    if (pendingOperations > 0) {
      return {
        icon: <Clock className="w-4 h-4" />,
        text: `${pendingOperations} pending`,
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200'
      };
    }
    
    if (failedOperations > 0) {
      return {
        icon: <XCircle className="w-4 h-4" />,
        text: `${failedOperations} failed`,
        color: 'bg-red-100 text-red-800 border-red-200'
      };
    }
    
    if (lastSyncTime && isOnline) {
      return {
        icon: <CheckCircle className="w-4 h-4" />,
        text: 'Synced',
        color: 'bg-green-100 text-green-800 border-green-200'
      };
    }
    
    return {
      icon: <WifiOff className="w-4 h-4" />,
      text: 'Offline',
      color: 'bg-gray-100 text-gray-800 border-gray-200'
    };
  };

  const statusInfo = getStatusInfo();

  return (
    <div className={`inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm font-medium border ${statusInfo.color}`}>
      {statusInfo.icon}
      <span>{statusInfo.text}</span>
    </div>
  );
}

/**
 * Loading overlay for offline operations
 */
export function OfflineLoadingOverlay() {
  const { isLoading } = useLoadingState();
  const { isSyncing, syncProgress } = useSyncStatus();

  if (!isLoading && !isSyncing) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
        <div className="flex items-center gap-3 mb-4">
          <RefreshCw className="w-6 h-6 text-blue-600 animate-spin" />
          <span className="font-medium text-gray-900">
            {isSyncing ? 'Syncing data...' : 'Loading...'}
          </span>
        </div>
        
        {syncProgress && (
          <>
            <div className="text-sm text-gray-600 mb-3">
              {syncProgress.stage}
            </div>
            
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                style={{ width: `${(syncProgress.current / syncProgress.total) * 100}%` }}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Error notification component
 */
export function OfflineErrorNotification() {
  const { hasErrors, syncError, dataError, initializationError, clearErrors } = useErrorState();

  if (!hasErrors) return null;

  const getErrorMessage = () => {
    if (initializationError) return `Initialization failed: ${initializationError}`;
    if (syncError) return `Sync failed: ${syncError}`;
    if (dataError) return `Data error: ${dataError}`;
    return 'An unknown error occurred';
  };

  return (
    <div className="fixed top-4 left-4 right-4 z-50 bg-red-50 border border-red-200 rounded-lg p-4">
      <div className="flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <h4 className="font-medium text-red-900 mb-1">Error</h4>
          <p className="text-sm text-red-700">{getErrorMessage()}</p>
        </div>
        <button
          onClick={clearErrors}
          className="text-red-600 hover:text-red-800 text-sm font-medium"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

/**
 * Offline data status component
 */
interface OfflineDataStatusProps {
  className?: string;
}

export function OfflineDataStatus({ className = '' }: OfflineDataStatusProps) {
  const { state } = useOfflineState();
  const { isOnline } = useNetworkStatus();

  const getStatusMessage = () => {
    if (!isOnline && !state.hasLocalData) {
      return 'No internet connection and no offline data available';
    }
    
    if (!isOnline && state.hasLocalData) {
      return 'Using offline data - some features may be limited';
    }
    
    if (isOnline && state.isDataStale) {
      return 'Data may be outdated - syncing in background';
    }
    
    return null;
  };

  const statusMessage = getStatusMessage();
  
  if (!statusMessage) return null;

  return (
    <div className={`bg-yellow-50 border border-yellow-200 rounded-lg p-3 mb-4 ${className}`}>
      <div className="flex items-center gap-2">
        <AlertCircle className="w-4 h-4 text-yellow-600" />
        <span className="text-sm text-yellow-800">{statusMessage}</span>
      </div>
    </div>
  );
}

/**
 * Offline action button wrapper
 */
interface OfflineActionButtonProps {
  children: React.ReactNode;
  requiresOnline?: boolean;
  onOfflineClick?: () => void;
  className?: string;
  disabled?: boolean;
}

export function OfflineActionButton({ 
  children, 
  requiresOnline = false, 
  onOfflineClick,
  className = '',
  disabled = false,
  ...props 
}: OfflineActionButtonProps & React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { isOnline } = useNetworkStatus();
  const { isSyncing } = useSyncStatus();

  const isDisabled = disabled || (requiresOnline && !isOnline) || isSyncing;

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (requiresOnline && !isOnline) {
      e.preventDefault();
      onOfflineClick?.();
      return;
    }
    
    props.onClick?.(e);
  };

  return (
    <button
      {...props}
      onClick={handleClick}
      disabled={isDisabled}
      className={`${className} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      {children}
      {requiresOnline && !isOnline && (
        <WifiOff className="w-4 h-4 ml-2 inline" />
      )}
    </button>
  );
}

/**
 * Offline wrapper component for conditional rendering
 */
interface OfflineWrapperProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  requiresOnline?: boolean;
  requiresData?: boolean;
}

export function OfflineWrapper({ 
  children, 
  fallback, 
  requiresOnline = false, 
  requiresData = false 
}: OfflineWrapperProps) {
  const { isOnline } = useNetworkStatus();
  const { state } = useOfflineState();

  const shouldShowFallback = 
    (requiresOnline && !isOnline) || 
    (requiresData && !state.hasLocalData);

  if (shouldShowFallback) {
    return fallback ? <>{fallback}</> : (
      <div className="text-center py-8 text-gray-500">
        <WifiOff className="w-8 h-8 mx-auto mb-2" />
        <p>This feature requires an internet connection</p>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Comprehensive offline UI provider
 */
export function OfflineUIProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      {children}
      <SyncProgressIndicator />
      <OfflineErrorNotification />
    </>
  );
}
